import React, { useState } from "react";
import { Col, Image } from "react-bootstrap";
import VerificationInput from "react-verification-input";

import "./ReferralCodePage.scss";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import OnboardingHeader from "../../../components/Headers/OnboardingHeader/OnboardingHeader";
import HyperWarningPill from "../../../components/Pill/HyperWarningPill";
import {
  clearStoredReferrerInfo,
  getStoredAuthProvider,
  getStoredReferrerInfo,
} from "../../../utils/storage";
import { OnboardingSteps, TRACKING_EVENTS } from "../../../utils/constants";
import { handleCreateReferral } from "../../../api-clients/referralsClient";
import { CustomSpinner } from "../../../components/CustomSpinner/CustomSpinner";
import { registerTrackingEvent, getCurrentUrl } from "../../../utils/helpers";
import { handleUpdateOnboardingState } from "../../../api-clients/userAccountClient";
import ImageGem from "../../../images/gem.svg";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import useAuth from "../../../hooks/useAuth";
import useAnalytics from "../../../hooks/useAnalytics";
import {
  ReferralOptionProperty,
  SIGNUP_METHODS,
} from "../../../utils/analytics";
import { getGamerInfoFromShortCode } from "../../../api-clients/urlsClient";

const CODE_LENGTH = 4;
const REFERRER_QUERY_NAME = "referrer";
const storedReferrerInfo = getStoredReferrerInfo();

function ReferralCodePage() {
  const [referralCode, setReferralCode] = useState(
    storedReferrerInfo?.code || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const { userProfile } = useAuth();
  const { goToStep } = useOnboardingSteps();
  const authProvider = getStoredAuthProvider();
  const { sendReferralOptionsEvent, sendReferredByEvent } = useAnalytics();

  const isReferralCodeComplete = referralCode.length === CODE_LENGTH;
  const referrerUsername = storedReferrerInfo?.username;

  const pillText = referrerUsername
    ? `You're using @${referrerUsername}'s referral code`
    : "Referral code is case sensitive. Make sure to confirm codes.";

  const handleSubmitCode = async () => {
    if (!userProfile) return;
    setIsLoading(true);
    const referrer = await getGamerInfoFromShortCode(referralCode);
    if (!referrer) {
      setIsLoading(false);
      return;
    }

    const { fullUrl: referralLink, username: referrerUsername } = referrer;
    const referralLinkParams = new URL(referralLink).search;
    const referrerId = new URLSearchParams(referralLinkParams).get(
      REFERRER_QUERY_NAME,
    );
    if (!referrerId) {
      setIsLoading(false);
      return;
    }

    const createdReferral = await handleCreateReferral({
      referrerId,
      referentId: userProfile.id,
    });
    clearStoredReferrerInfo();
    if (!createdReferral) {
      setIsLoading(false);
      return;
    }

    await handleUpdateOnboardingState({ usedReferralCode: true });
    if (authProvider) {
      sendReferredByEvent({
        referredBy: referrerId,
        referredBy_username: referrerUsername ?? "",
        signup_method: SIGNUP_METHODS[authProvider],
      });
    }
    registerTrackingEvent({
      name: TRACKING_EVENTS.WEB_SUBMIT_REFERRAL_CODE,
      payload: {
        user_id: userProfile.id,
        email: userProfile.email,
        referral_username: referrerUsername,
        referral_userID: referrerId,
        url: getCurrentUrl(),
      },
    });
    setIsLoading(false);
    goToStep(OnboardingSteps.CLAIM_USERNAME);
  };

  const handleContinue = () => {
    const authProvider = getStoredAuthProvider();
    if (authProvider) {
      sendReferralOptionsEvent({
        referral_option: ReferralOptionProperty.CONTINUE,
        signup_method: SIGNUP_METHODS[authProvider],
      });
    }
    handleSubmitCode();
  };

  const handleSkip = async () => {
    await handleUpdateOnboardingState({ usedReferralCode: true });
    if (authProvider) {
      sendReferralOptionsEvent({
        referral_option: ReferralOptionProperty.SKIP,
        signup_method: SIGNUP_METHODS[authProvider],
      });
    }
    registerTrackingEvent({
      name: TRACKING_EVENTS.SKIP_WEB_REFERRAL,
      payload: {
        user_id: userProfile?.id,
        email: userProfile?.email,
        url: getCurrentUrl(),
      },
    });
    clearStoredReferrerInfo();
    goToStep(OnboardingSteps.CLAIM_USERNAME);
  };

  return (
    <>
      <div className="MobileOnly position-relative referral-code-page">
        <Col className="d-flex">
          <Col className="referral-screen-header">
            <OnboardingHeader amountActive={3} />
          </Col>

          <Col className="flex-column referral-code-page-content">
            <p className="mx-3 text-white page-title">Got a referral code?</p>
            <p className="mx-3 text-white page-description">
              You friend gets rewarded with <Image src={ImageGem} alt="gem" />{" "}
              GEM when you use their referral code.
            </p>
            <Col className="mx-3 pin-text-container">
              <VerificationInput
                length={CODE_LENGTH}
                placeholder=""
                classNames={{
                  container: "character-container",
                  character: "character",
                  characterSelected: "character--selected",
                }}
                value={referralCode}
                onChange={(value) => setReferralCode(value)}
              />
            </Col>
            <HyperWarningPill
              textClass="pill-text"
              text={pillText}
              style={{ margin: "0 16px" }}
            />
            <Col className="referral-code-footer MobileOnly">
              <PrimaryActionButton
                text={isLoading ? <CustomSpinner /> : "Continue"}
                onClick={handleContinue}
                imgClassName="mb-1 mx-2"
                disabled={!isReferralCodeComplete || isLoading}
                style={{ marginTop: "16px" }}
              />
              <PrimaryActionButton
                text="I don't have a code"
                onClick={handleSkip}
                imgClassName="mb-1 mx-2"
                style={{ background: "#191040", marginTop: "16px" }}
              />
            </Col>
          </Col>
        </Col>
      </div>
    </>
  );
}

export default ReferralCodePage;
